import beta from "./data/beta.json" with { type: "json" };
import {
  planUnits,
  unitStats,
  type AttackPlan,
  type UnitKind,
  type UnitCounts,
} from "@empire/core-sim";
import { type BuildingLevels, productionPerHour } from "./production.js";
import { buildingStats, emptyResources, RESOURCE_KINDS, type Resources } from "./economy.js";

export const BALANCE = beta;
export const RULES_VERSION = beta.rulesVersion;
export function unitSupply(kind: UnitKind): number {
  return Math.max(1, Math.ceil(unitStats(kind).totalCost / beta.supplyResourceDivisor));
}
export function armySupply(army: UnitCounts): number {
  return Object.entries(army).reduce(
    (sum, [kind, count]) => sum + (count ?? 0) * unitSupply(kind as UnitKind),
    0
  );
}
export function deploymentLimit(levels: BuildingLevels): number {
  return (
    beta.deploymentBase +
    beta.deploymentPerRallyLevel *
      Math.max(0, Math.min(buildingStats("rally_point").maxLevel, levels.rally_point ?? 0))
  );
}
export function deploymentProblem(plan: AttackPlan, limit: number): string | null {
  if (plan.waves.some((w) => w.units.includes("scout")))
    return "Scouts cannot join raids. Dedicated scouting is not available yet.";
  const used = armySupply(planUnits(plan));
  return used > limit
    ? `This army needs ${used} supply; your Rally Point allows ${limit}. Send fewer troops or upgrade it.`
    : null;
}
export function beginnerBuildMultiplier(targetLevel: number): number {
  return targetLevel <= beta.beginnerFastLevel
    ? beta.beginnerFastMultiplier
    : targetLevel <= beta.beginnerMediumLevel
      ? beta.beginnerMediumMultiplier
      : 1;
}
export function victimAllowance(levels: BuildingLevels, recentLoss: Resources): Resources {
  const production = productionPerHour(levels);
  const out = emptyResources();
  for (const k of RESOURCE_KINDS)
    out[k] = Math.max(
      0,
      Math.max(beta.victimMinimumLossCap, production[k] * beta.victimLossProductionHours) -
        recentLoss[k]
    );
  return out;
}
export function replacementCost(losses: UnitCounts): Resources {
  const out = emptyResources();
  for (const [kind, count] of Object.entries(losses)) {
    const cost = unitStats(kind as UnitKind).cost;
    for (const k of RESOURCE_KINDS) out[k] += (cost[k] ?? 0) * (count ?? 0);
  }
  return out;
}
export function canCancel(startedAtMs: number, nowMs: number): boolean {
  return nowMs <= startedAtMs + beta.cancellationGraceSeconds * 1000;
}
